import { format, addDays, isAfter, isBefore, isEqual } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

// Singapore timezone constant
const SINGAPORE_TIMEZONE = 'Asia/Singapore';

// Interface for schedule data when creating new items
interface ScheduleData {
  doctorId?: string;
  staffId?: string;
  roomId?: string;
  startTime: string;
  endTime: string;
  type: 'appointment' | 'admin' | 'break' | 'leave' | 'mc';
  title: string;
  notes?: string;
  procedure?: string;
  adminTask?: string;
  endDate?: string;
  clinic_id?: string;
  external_source?: string;
  external_id?: string;
}

/**
 * Get current date in Singapore timezone
 */
export const getCurrentSingaporeDate = (): Date => {
  return toZonedTime(new Date(), SINGAPORE_TIMEZONE);
};

/**
 * Convert any date to Singapore timezone
 */
export const toSingaporeTime = (date: Date): Date => {
  return toZonedTime(date, SINGAPORE_TIMEZONE);
};

/**
 * Format date to YYYY-MM-DD string in Singapore timezone
 */
export const formatDateToString = (date: Date): string => {
  const singaporeDate = toSingaporeTime(date);
  return format(singaporeDate, 'yyyy-MM-dd');
};

/**
 * Parse date string to Date object in Singapore timezone
 */
export const parseDateString = (dateString: string): Date => {
  // Create date at midnight Singapore time
  const [year, month, day] = dateString.split('-').map(Number);
  const localDate = new Date(year, month - 1, day);
  return toSingaporeTime(localDate);
};

/**
 * Check if two dates are the same day in Singapore timezone
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  const sg1 = toSingaporeTime(date1);
  const sg2 = toSingaporeTime(date2);
  return format(sg1, 'yyyy-MM-dd') === format(sg2, 'yyyy-MM-dd');
};

/**
 * Get today's date string in Singapore timezone
 */
export const getTodayString = (): string => {
  return formatDateToString(getCurrentSingaporeDate());
};

/**
 * Check if a date is today in Singapore timezone
 */
export const isToday = (date: Date): boolean => {
  return isSameDay(date, getCurrentSingaporeDate());
};

/**
 * Generate date range between two dates in Singapore timezone
 */
export const generateDateRange = (startDate: Date, endDate?: Date): string[] => {
  const singaporeStartDate = toSingaporeTime(startDate);
  
  if (!endDate) {
    return [formatDateToString(singaporeStartDate)];
  }

  const singaporeEndDate = toSingaporeTime(endDate);
  const dates: string[] = [];
  const currentDate = new Date(singaporeStartDate);

  while (currentDate <= singaporeEndDate) {
    dates.push(formatDateToString(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

/**
 * Creates multiple schedule items for a date range based on the base schedule data
 */
export const createScheduleItemsForDateRange = (
  baseScheduleData: Omit<ScheduleData, 'date'>,
  dateRange: string[],
  originalStartTime: string,
  originalEndTime: string
): (ScheduleData & { date: string })[] => {
  const scheduleItems = dateRange.map((date) => {
    const item = {
      ...baseScheduleData,
      date,
      startTime: originalStartTime,
      endTime: originalEndTime,
    };
    
    return item;
  });

  return scheduleItems;
};

/**
 * Get start of week for a given date in Singapore timezone
 */
export const getStartOfWeek = (date: Date): Date => {
  const singaporeDate = toSingaporeTime(date);
  const startOfWeek = new Date(singaporeDate);
  const day = startOfWeek.getDay();
  startOfWeek.setDate(startOfWeek.getDate() - day); // Go back to Sunday
  return toSingaporeTime(startOfWeek);
};

/**
 * Get end of week for a given date in Singapore timezone
 */
export const getEndOfWeek = (date: Date): Date => {
  const startOfWeek = getStartOfWeek(date);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  return toSingaporeTime(endOfWeek);
};

/**
 * Get first day of month in Singapore timezone
 */
export const getFirstDayOfMonth = (date: Date): Date => {
  const singaporeDate = toSingaporeTime(date);
  return toSingaporeTime(new Date(singaporeDate.getFullYear(), singaporeDate.getMonth(), 1));
};

/**
 * Get last day of month in Singapore timezone
 */
export const getLastDayOfMonth = (date: Date): Date => {
  const singaporeDate = toSingaporeTime(date);
  return toSingaporeTime(new Date(singaporeDate.getFullYear(), singaporeDate.getMonth() + 1, 0));
};

/**
 * Add days to a date in Singapore timezone
 */
export const addDaysInSingapore = (date: Date, days: number): Date => {
  const singaporeDate = toSingaporeTime(date);
  const newDate = addDays(singaporeDate, days);
  return toSingaporeTime(newDate);
};

/**
 * Get week days array starting from a given date in Singapore timezone
 */
export const getWeekDays = (startDate: Date): Date[] => {
  const startOfWeek = getStartOfWeek(startDate);
  const days: Date[] = [];
  
  for (let i = 0; i < 7; i++) {
    const day = addDays(startOfWeek, i);
    days.push(toSingaporeTime(day));
  }
  
  return days;
};

/**
 * Get calendar days for monthly view (42 days total including prev/next month)
 */
export const getCalendarDays = (date: Date): { date: Date; isCurrentMonth: boolean }[] => {
  const singaporeDate = toSingaporeTime(date);
  const year = singaporeDate.getFullYear();
  const month = singaporeDate.getMonth();
  
  const firstDayOfMonth = toSingaporeTime(new Date(year, month, 1));
  const lastDayOfMonth = toSingaporeTime(new Date(year, month + 1, 0));
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  
  const calendarDays: { date: Date; isCurrentMonth: boolean }[] = [];
  
  // Previous month days
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    calendarDays.push({
      date: toSingaporeTime(new Date(year, month - 1, daysInPrevMonth - i)),
      isCurrentMonth: false
    });
  }
  
  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      date: toSingaporeTime(new Date(year, month, day)),
      isCurrentMonth: true
    });
  }
  
  // Next month days
  const remainingDays = 42 - calendarDays.length;
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      date: toSingaporeTime(new Date(year, month + 1, day)),
      isCurrentMonth: false
    });
  }

  return calendarDays;
};

/**
 * Expands multi-day schedules into individual day entries for display
 * This creates virtual schedule items for each day in a multi-day range
 */
export const expandMultiDaySchedules = (
  schedules: any[],
  viewStartDate?: string,
  viewEndDate?: string
): any[] => {
  const expanded: any[] = [];
  const processedIds = new Set<string>();
  
  schedules.forEach(schedule => {
    // Skip if we've already processed this base schedule
    const baseId = schedule.id.split('_day_')[0];
    if (processedIds.has(baseId) && schedule.id.includes('_day_')) {
      return;
    }
    
    if (schedule.endDate && schedule.date !== schedule.endDate) {
      // Multi-day schedule - expand it
      const dates = generateDateRange(new Date(schedule.date), new Date(schedule.endDate));
      processedIds.add(schedule.id);
      
      dates.forEach((date, index) => {
        // Only include dates within view range if specified
        if ((!viewStartDate || date >= viewStartDate) && (!viewEndDate || date <= viewEndDate)) {
          const dayNumber = index + 1;
          const totalDays = dates.length;
          
          // Create a virtual schedule item for this day
          expanded.push({
            ...schedule,
            date,
            id: `${schedule.id}_day_${dayNumber}`, // Virtual ID for display
            isMultiDay: true,
            dayNumber,
            totalDays,
            originalId: schedule.id,
            // Add day indicator to title for leave/mc types
            title: (schedule.type === 'leave' || schedule.type === 'mc') 
              ? `${schedule.title} (Day ${dayNumber}/${totalDays})`
              : schedule.title
          });
        }
      });
    } else {
      // Single day schedule or already expanded item
      if (!schedule.id.includes('_day_')) {
        expanded.push(schedule);
      }
    }
  });
  
  return expanded;
};
